import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users, Calendar, FileText, BarChart3, Workflow, Settings, Upload, RefreshCw } from "lucide-react";
import ApiService from '../../services/apiService';

const RightSidebar = ({ onNavigate }) => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 6)); // July 2025
  const [selectedDay, setSelectedDay] = useState(28);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const apiService = new ApiService();

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // Get lesson plan which includes events/activities
      const weekStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const weekEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const weekString = `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      
      const lessonPlan = await apiService.generateLessonPlan(weekString, ['3', '4', '5'], 'General');
      
      // Transform lesson plan data to events
      const upcomingEvents = [
        {
          title: "Workflow Demo Session",
          date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 28).toLocaleDateString(),
          time: "09:15AM - 10:50AM",
          icon: Workflow,
          color: "text-purple-600",
        },
        {
          title: "Content Generation Training",
          date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 29).toLocaleDateString(), 
          time: "11:00AM - 12:30PM",
          icon: FileText,
          color: "text-blue-600",
        },
        {
          title: "AI Agent Review Session",
          date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 30).toLocaleDateString(),
          time: "02:00PM - 03:30PM",
          icon: Users,
          color: "text-green-600",
        },
      ];
      
      setEvents(upcomingEvents);
    } catch (error) {
      console.warn('Failed to fetch events, using fallback data');
      // Fallback events
      setEvents([
        {
          title: "Workflow Demo Session",
          date: "28 July 2025",
          time: "09:15AM - 10:50AM",
          icon: Workflow,
          color: "text-purple-600",
        },
        {
          title: "Content Generation Training",
          date: "29 July 2025", 
          time: "11:00AM - 12:30PM",
          icon: FileText,
          color: "text-blue-600",
        },
        {
          title: "Student Progress Review",
          date: "30 July 2025",
          time: "02:00PM - 03:30PM",
          icon: Users,
          color: "text-green-600",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const actionButtons = [
    { 
      icon: Workflow, 
      label: "Create Flow", 
      color: "bg-purple-500",
      action: "workflow" 
    },
    { 
      icon: Upload, 
      label: "Upload Media", 
      color: "bg-indigo-500",
      action: "upload-image" 
    },
    { 
      icon: BarChart3, 
      label: "Analytics", 
      color: "bg-blue-500",
      action: "student-tracker" 
    },
    { 
      icon: Settings, 
      label: "Settings", 
      color: "bg-gray-500",
      action: "settings" 
    },
  ];

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate) === 0 ? 6 : getFirstDayOfMonth(currentDate) - 1;
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = day === selectedDay;
      const isHovered = day === hoveredDay;
      const hasEvent = [28, 29, 30].includes(day); // Days with events

      days.push(
        <button
          key={day}
          onClick={() => setSelectedDay(day)}
          onMouseEnter={() => setHoveredDay(day)}
          onMouseLeave={() => setHoveredDay(null)}
          className={`h-8 w-8 rounded-lg text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative group ${
            isSelected
              ? "bg-blue-600 text-white shadow-lg transform scale-110 z-10"
              : isHovered
                ? "bg-blue-100 text-blue-600 transform scale-105"
                : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
          }`}
        >
          {day}
          {hasEvent && (
            <div
              className={`absolute -top-1 -right-1 w-2 h-2 rounded-full transition-all duration-200 ${
                isSelected ? "bg-white" : "bg-blue-500"
              }`}
            ></div>
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="space-y-6 sticky top-6">
      {/* Calendar */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
      </div>

      {/* Upcoming Events */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-lg">Upcoming Events</h3>
          <button 
            onClick={fetchEvents}
            disabled={loading}
            className="text-blue-600 hover:text-blue-700 p-1 rounded transition-colors duration-200 disabled:opacity-50"
            title="Refresh Events"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="space-y-3">
          {events.map((event, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 group cursor-pointer transform hover:-translate-y-1 hover:shadow-md"
            >
              <div className="p-2 rounded-lg bg-white shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-110">
                <event.icon className={`w-4 h-4 ${event.color} transition-colors duration-200`} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 text-sm leading-tight group-hover:text-blue-600 transition-colors duration-200">
                  {event.title}
                </h4>
                <p className="text-gray-600 text-xs mt-1">{event.date}</p>
                <p className="text-gray-500 text-xs">{event.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {actionButtons.map((button, index) => (
          <button
            key={index}
            onClick={() => onNavigate && onNavigate({ id: button.action, label: button.label })}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 group text-center active:scale-95"
          >
            <div
              className={`w-10 h-10 ${button.color} rounded-lg flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-all duration-300 group-hover:shadow-lg`}
            >
              <button.icon className="w-5 h-5 text-white" />
            </div>
            <p className="font-medium text-gray-900 text-sm group-hover:text-blue-600 transition-colors duration-200">
              {button.label}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RightSidebar;